import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import TvCard from '@/components/feature/TvCard';
import SEO from '@/components/base/SEO';
import { tvSeries, categories, featuredShows, topRatedShows, siteInfo } from '@/mocks/tvSeries';

export default function Home() {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [location.pathname]);

  const latestShows = [...tvSeries].sort((a, b) => b.year - a.year).slice(0, 8);

  const homeSchema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        name: siteInfo.name,
        url: 'https://caomin.tv',
        description: siteInfo.description,
        email: siteInfo.email,
        foundingDate: siteInfo.founded,
        slogan: siteInfo.slogan,
      },
      {
        '@type': 'WebSite',
        name: siteInfo.name,
        url: 'https://caomin.tv',
        description: siteInfo.description,
        potentialAction: {
          '@type': 'SearchAction',
          target: 'https://caomin.tv/search?q={search_term_string}',
          'query-input': 'required name=search_term_string',
        },
      },
      {
        '@type': 'WebPage',
        name: `${siteInfo.name} - 首页`,
        description: siteInfo.description,
        url: 'https://caomin.tv',
        isPartOf: {
          '@type': 'WebSite',
          name: siteInfo.name,
          url: 'https://caomin.tv',
        },
      },
    ],
  };

  return (
    <div className="min-h-screen bg-background-50">
      <SEO
        schema={homeSchema}
      />

      <Navbar />

      <main>
        {/* Hero Section */}
        <section className="relative w-full h-[420px] md:h-[600px] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=A%20cinematic%20dark%20theater%20interior%20with%20warm%20golden%20spotlights%20illuminating%20red%20velvet%20curtains%2C%20art%20deco%20theater%20architecture%2C%20elegant%20cinema%20atmosphere%2C%20dramatic%20lighting%2C%20luxurious%20and%20nostalgic%20movie%20theater%20vibe%2C%20rich%20warm%20amber%20tones%2C%20high%20detail%20atmospheric%20rendering&width=1600&height=900&seq=hero-bg-cinema&orientation=landscape"
            alt="草民影院"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/60" />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-full text-center px-4">
              <h1 className="text-3xl md:text-5xl font-bold font-heading text-white mb-4 tracking-wide">
                草民影院
              </h1>
              <p className="text-base md:text-xl text-white/80 mb-2 max-w-2xl mx-auto">
                电视剧全集、最新电视剧、热门剧集
              </p>
              <p className="text-sm md:text-lg text-white/60 mb-8 max-w-xl mx-auto">
                国产剧 · 韩剧 · 美剧 · 日剧 · 泰剧 · 动漫 — 高清不卡，即点即看
              </p>
              <div className="flex items-center justify-center gap-3 flex-wrap">
                <Link
                  to="/category/guochanju"
                  className="px-6 py-3 bg-primary-500 text-background-50 rounded-lg text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  立即观看
                </Link>
                <Link
                  to="/category/all"
                  className="px-6 py-3 bg-background-50/20 text-white backdrop-blur-sm rounded-lg text-sm font-semibold hover:bg-background-50/30 transition-colors whitespace-nowrap cursor-pointer"
                >
                  全部剧集
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Category Navigation */}
        <section className="max-w-[1280px] mx-auto px-4 md:px-6 -mt-8 relative z-10">
          <div className="bg-background-50 rounded-xl p-2 border border-background-200/70">
            <div className="flex items-center gap-1 overflow-x-auto flex-wrap">
              {categories.map(cat => (
                <Link
                  key={cat.slug}
                  to={cat.slug === 'all' ? '/category/all' : `/category/${cat.slug}`}
                  className="px-4 py-2 text-sm font-medium rounded-lg whitespace-nowrap transition-colors cursor-pointer text-foreground-700 hover:bg-background-100 hover:text-foreground-900"
                >
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Featured Shows */}
        <section className="max-w-[1280px] mx-auto px-4 md:px-6 py-10 md:py-14">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900">
              热门推荐
            </h2>
            <Link
              to="/category/all"
              className="text-sm text-primary-600 hover:text-primary-700 transition-colors flex items-center gap-1 whitespace-nowrap"
            >
              查看全部
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-line text-sm"></i>
              </span>
            </Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5">
            {featuredShows.map(show => (
              <TvCard key={show.id} {...show} />
            ))}
          </div>
        </section>

        {/* Top Rated */}
        <section className="bg-background-100 py-10 md:py-14">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900">
                高分必看
              </h2>
              <Link
                to="/category/all"
                className="text-sm text-primary-600 hover:text-primary-700 transition-colors flex items-center gap-1 whitespace-nowrap"
              >
                查看全部
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line text-sm"></i>
                </span>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
              {topRatedShows.map(show => (
                <TvCard key={show.id} {...show} />
              ))}
            </div>
          </div>
        </section>

        {/* Latest Episodes */}
        <section className="max-w-[1280px] mx-auto px-4 md:px-6 py-10 md:py-14">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900">
              最新上架
            </h2>
            <Link
              to="/category/all"
              className="text-sm text-primary-600 hover:text-primary-700 transition-colors flex items-center gap-1 whitespace-nowrap"
            >
              查看全部
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-line text-sm"></i>
              </span>
            </Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
            {latestShows.map(show => (
              <TvCard key={show.id} {...show} />
            ))}
          </div>
        </section>

        {/* Features */}
        <section className="bg-background-100 py-10 md:py-14">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900 text-center mb-10">
              为什么选择草民影院
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { icon: 'ri-4k-line', title: '高清画质', desc: '支持超清1080P画质，带来极致观影体验' },
                { icon: 'ri-flashlight-line', title: '即点即看', desc: '无需等待缓冲，点击即可流畅播放' },
                { icon: 'ri-global-line', title: '海量资源', desc: '涵盖国产剧、韩剧、美剧等丰富内容' },
                { icon: 'ri-heart-line', title: '每日更新', desc: '热门剧集每日同步更新，追剧不等待' },
              ].map((feature, idx) => (
                <div key={idx} className="text-center p-6">
                  <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                    <i className={`${feature.icon} text-2xl`}></i>
                  </div>
                  <h3 className="text-base font-semibold text-foreground-900 mb-2">{feature.title}</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{feature.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}